import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiCloudLine } from '@remixicon/react';
import {
  ExternalLink,
  InfraCodeBlock,
  InfraField,
  InfraSection,
  InfraStack,
  InfraTag,
  InfraTagValue,
  InfraTagRow,
  Text,
} from '../ui/InfraPrimitives';

export const RedisCloudVPCPeeringInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const subscriptionId = annotations['redis-cloud.infrastructure/subscription-id'];
  const subscriptionCidr = annotations['redis-cloud.infrastructure/subscription-cidr'];
  const redisAwsAccountId = annotations['redis-cloud.infrastructure/redis-aws-account-id'];
  const redisRegion = annotations['redis-cloud.infrastructure/redis-region'];
  const peeringConnectionId = annotations['redis-cloud.infrastructure/peering-connection-id'];
  const peeringStatus = annotations['redis-cloud.infrastructure/peering-status'];
  const routesCreated = annotations['redis-cloud.infrastructure/routes-created'];
  const vpcId = annotations['aws.infrastructure/vpc-id'];
  const awsRegion = annotations['aws.infrastructure/region'];
  const securityGroupId = annotations['aws.infrastructure/security-group-id'];

  const peeringId = annotations['redis-cloud.infrastructure/peering-id'];
  const peeringName = annotations['redis-cloud.infrastructure/peering-name'];
  const redisProjectId = annotations['redis-cloud.infrastructure/redis-project-id'];
  const redisNetworkName = annotations['redis-cloud.infrastructure/redis-network-name'];
  const gcpProjectId = annotations['gcp.infrastructure/project-id'];
  const gcpNetworkName = annotations['gcp.infrastructure/network-name'];
  const gcpRegion = annotations['gcp.infrastructure/region'];

  const pscServiceId = annotations['redis-cloud-psc.infrastructure/service-id'];
  const isPSC = !!pscServiceId;

  const isGCP =
    peeringId || peeringName || redisProjectId || redisNetworkName || gcpNetworkName;

  const hasRedisCloudInfo =
    subscriptionId || peeringConnectionId || peeringId || peeringName;

  if (!hasRedisCloudInfo || isPSC) {
    return null;
  }

  return (
    <InfoCard
      title="Redis Cloud VPC Peering Information"
      icon={<RiCloudLine size={20} />}
    >
      <InfraStack>
        <InfraSection title="☁️ Redis Cloud Subscription">
          {subscriptionId && (
            <InfraField label="Subscription ID:" value={subscriptionId} />
          )}
          {subscriptionCidr && (
            <InfraField
              label="Subscription CIDR:"
              value={<InfraTagValue>{subscriptionCidr}</InfraTagValue>}
              mono={false}
            />
          )}
          {!isGCP && redisAwsAccountId && (
            <InfraField label="Redis AWS Account ID:" value={redisAwsAccountId} />
          )}
          {!isGCP && redisRegion && (
            <InfraField
              label="Redis Region:"
              value={<InfraTagValue>{redisRegion}</InfraTagValue>}
              mono={false}
            />
          )}
          {isGCP && redisProjectId && (
            <InfraField label="Redis Cloud Project ID:" value={redisProjectId} />
          )}
          {isGCP && redisNetworkName && (
            <InfraField label="Redis Cloud Network:" value={redisNetworkName} />
          )}
        </InfraSection>

        <InfraSection title={isGCP ? '🔗 GCP VPC' : '🔗 AWS VPC'}>
          {isGCP ? (
            <>
              {gcpProjectId && (
                <InfraField label="GCP Project ID:" value={gcpProjectId} />
              )}
              {gcpNetworkName && (
                <InfraField label="VPC Network:" value={gcpNetworkName} />
              )}
              {gcpRegion && (
                <InfraField
                  label="GCP Region:"
                  value={<InfraTagValue>{gcpRegion}</InfraTagValue>}
                  mono={false}
                />
              )}
            </>
          ) : (
            <>
              {vpcId && <InfraField label="VPC ID:" value={vpcId} />}
              {awsRegion && (
                <InfraField
                  label="AWS Region:"
                  value={<InfraTagValue>{awsRegion}</InfraTagValue>}
                  mono={false}
                />
              )}
              {securityGroupId && (
                <InfraField label="Security Group ID:" value={securityGroupId} />
              )}
            </>
          )}
        </InfraSection>

        <InfraSection title="🔄 VPC Peering Status">
          {peeringConnectionId && (
            <InfraField label="Peering Connection ID:" value={peeringConnectionId} />
          )}
          {peeringId && <InfraField label="Peering ID:" value={peeringId} />}
          {peeringName && <InfraField label="Peering Name:" value={peeringName} />}
          {peeringStatus && (
            <InfraField
              label="Status:"
              value={
                <InfraTagRow>
                  <InfraTag>{peeringStatus}</InfraTag>
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {routesCreated && (
            <InfraField
              label="Routes Created:"
              value={<InfraTagValue>{`${routesCreated} route table(s)`}</InfraTagValue>}
              mono={false}
            />
          )}
        </InfraSection>

        <InfraSection title="🚀 Quick Start">
          {isGCP ? (
            <>
              <Text variant="body-medium">
                Your VPC Peering connection is established! You can now access Redis Cloud
                databases from your GCP VPC (GCE, GKE, Cloud Run, etc.).
              </Text>

              <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                Test connectivity from a GCE instance or GKE pod:
              </Text>
              <InfraCodeBlock variant="light">
                {`# From GCE instance (or gcloud compute ssh)
sudo apt-get update && sudo apt-get install -y redis-tools

# Test connection (replace with your database endpoint from Redis Cloud Console)
redis-cli -h <database-endpoint> -p <port> PING
# Expected output: PONG`}
              </InfraCodeBlock>

              <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                Or from any machine with gcloud:
              </Text>
              <InfraCodeBlock variant="light">
                {`# SSH into a GCE instance in your peered VPC
gcloud compute ssh <instance-name> \\
  --project=${gcpProjectId || '<project-id>'} \\
  --zone=<zone>

# Then run redis-cli to test connectivity`}
              </InfraCodeBlock>

              <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                Allowed Redis Ports:
              </Text>
              <InfraTagRow>
                <InfraTag>6379 (Redis)</InfraTag>
                <InfraTag>6380 (Redis SSL)</InfraTag>
                <InfraTag>10000-19999 (Redis Enterprise)</InfraTag>
              </InfraTagRow>

              <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                Next Steps:
              </Text>
              <ul>
                <li>
                  <Text variant="body-medium">
                    Get database endpoint from{' '}
                    <ExternalLink href="https://app.redislabs.com">
                      Redis Cloud Console
                    </ExternalLink>
                  </Text>
                </li>
                <li>
                  <Text variant="body-medium">
                    Ensure your GCE instances or GKE nodes are in the peered network:{' '}
                    <code>{gcpNetworkName || 'N/A'}</code>
                  </Text>
                </li>
                <li>
                  <Text variant="body-medium">
                    Verify routing: import/export custom routes if needed. Check{' '}
                    <ExternalLink href="https://cloud.google.com/vpc/docs/vpc-peering#import-export-custom-routes">
                      GCP VPC Peering docs
                    </ExternalLink>{' '}
                    for custom route configuration.
                  </Text>
                </li>
                <li>
                  <Text variant="body-medium">
                    View peering in{' '}
                    <ExternalLink
                      href={`https://console.cloud.google.com/networking/peering/list?project=${gcpProjectId || ''}`}
                    >
                      GCP Console → VPC Network Peering
                    </ExternalLink>
                  </Text>
                </li>
              </ul>
            </>
          ) : (
            <>
              <Text variant="body-medium">
                Your VPC Peering connection is established! You can now access Redis Cloud
                databases from your AWS VPC.
              </Text>

              <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                Test connectivity from an EC2 instance in your VPC:
              </Text>
              <InfraCodeBlock variant="light">
                {`# Install redis-cli
sudo apt-get update && sudo apt-get install -y redis-tools

# Test connection (replace with your database endpoint)
redis-cli -h <database-endpoint> -p <port> PING

# Expected output: PONG`}
              </InfraCodeBlock>

              <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                Allowed Redis Ports:
              </Text>
              <InfraTagRow>
                <InfraTag>6379 (Redis)</InfraTag>
                <InfraTag>6380 (Redis SSL)</InfraTag>
                <InfraTag>10000-19999 (Redis Enterprise)</InfraTag>
              </InfraTagRow>

              <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                Next Steps:
              </Text>
              <ul>
                <li>
                  <Text variant="body-medium">
                    Get database endpoint from{' '}
                    <ExternalLink href="https://app.redislabs.com">
                      Redis Cloud Console
                    </ExternalLink>
                  </Text>
                </li>
                <li>
                  <Text variant="body-medium">
                    Ensure your EC2 instances use the security group:{' '}
                    <code>{securityGroupId || 'N/A'}</code>
                  </Text>
                </li>
                <li>
                  <Text variant="body-medium">
                    Verify routes in your VPC route tables point to Redis Cloud CIDR:{' '}
                    <code>{subscriptionCidr || 'N/A'}</code>
                  </Text>
                </li>
              </ul>
            </>
          )}
        </InfraSection>
      </InfraStack>
    </InfoCard>
  );
};
